import { useCallback, useRef, useState, useEffect, useMemo } from 'react'
import throttle from 'lodash/throttle'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useI18n } from 'builder/hooks/useI18n'
import Logo from 'builder/components/Logo'
import ModalOverlay from 'builder/components/ModalOverlay'
import { PreviewRefProps } from 'builder/components/PreviewPanel'
import Preview from 'builder/components/Preview'
import { DocumentTypes } from 'builder/modules/constants'
import templateHeightFactorSelector from 'builder/components/Helper/utils/templateHeightFactorSelector'
import {
  selectors as resumeDistributionSelectors,
  actions as resumeDistributionActions,
  ResumeDetails,
} from 'builder/modules/resumeDistribution'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import Icon from 'builder/components/Icon'
import { typeToSlug } from 'builder/modules/panel'
import TREditModal from 'builder/components/TREditModal'
import { actions as uiActions } from 'builder/modules/ui'
import { LOCALIZATION_KEY } from '../../constants'
import {
  Container,
  HeaderContainer,
  BackButton,
  BackIcon,
  PreviewContainer,
  Pagination,
  PaginationPrev,
  PaginationCounter,
  PaginationNext,
  ContentInner,
  PreviewBox,
  PreviewWrapper,
  PreviewImage,
  EditButton,
} from './styles'

export const ResumePreviewModal = () => {
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [width, setWidth] = useState<number>(0)
  const [height, setHeight] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPagesNumber, setTotalPagesNumber] = useState<number>(1)
  const previewRef = useRef<PreviewRefProps>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const previewBoxRef = useRef<HTMLDivElement>(null)
  const selectedResumeDetails = useTypedSelector<ResumeDetails>(
    resumeDistributionSelectors.selectedResumeDetails,
  )

  const isTREditModalOpen = useTypedSelector(store => store.ui.isTREditModalOpen)

  const resume = useTypedSelector(resumeDistributionSelectors.formResumeFetchDetails)

  const hasPreviousPage = currentPage > 1
  const hasNextPage = currentPage < totalPagesNumber
  const editorUrl = `/${typeToSlug(DocumentTypes.resume)}/${
    resume?.id
  }/edit?resume-distribution-id=${resume?.id}`

  useEffect(() => {
    if (!containerRef.current) return

    const pdfHeightFactor = resume ? templateHeightFactorSelector(resume) : 1

    const handleResize = throttle(() => {
      if (!containerRef.current) return
      const previewHeight = containerRef.current.offsetHeight
      const previewWidth = containerRef.current.offsetWidth

      const width = Math.min(previewWidth, previewHeight * pdfHeightFactor)
      setWidth(Math.round(width))
      setHeight(Math.round(width / pdfHeightFactor))

      if (previewRef.current) {
        previewRef.current.resize(width)
      }
    }, 50)

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
      handleResize.cancel?.()
    }
  }, [resume])

  const handlePaginationStateChange = useCallback(
    (state: { currentPage: number; totalPagesCount: number }) => {
      const { currentPage, totalPagesCount } = state
      setCurrentPage(currentPage + 1)
      setTotalPagesNumber(totalPagesCount)
    },
    [],
  )

  const handleClosePreview = () => {
    dispatch(resumeDistributionActions.setResumePreviewModalStatus(false))
  }

  const handlePreviousPageClick = useCallback(() => {
    if (selectedResumeDetails?.type === DocumentTypes.trDocument) {
      setCurrentPage(currentPage - 1)
    } else {
      if (!previewRef.current) return
      previewRef.current?.previousPage()
    }
  }, [currentPage, selectedResumeDetails?.type])

  const handleNextPageClick = useCallback(() => {
    if (selectedResumeDetails?.type === DocumentTypes.trDocument) {
      setCurrentPage(currentPage + 1)
    } else {
      if (!previewRef.current) return
      previewRef.current?.nextPage()
    }
  }, [currentPage, selectedResumeDetails?.type])

  const handleEditResumeClick = useCallback(() => {
    if (selectedResumeDetails?.type === DocumentTypes.trDocument) {
      if ('id' in selectedResumeDetails && selectedResumeDetails.id) {
        dispatch(uiActions.openTREditModal(selectedResumeDetails.id))
      }
    } else {
      if (!resume) return
      navigate(editorUrl)
    }
  }, [dispatch, editorUrl, navigate, resume, selectedResumeDetails])

  const renderResume = useMemo(() => {
    if (!selectedResumeDetails) return null
    else if (selectedResumeDetails?.type === DocumentTypes.trDocument) {
      setTotalPagesNumber(
        'previews' in selectedResumeDetails ? selectedResumeDetails.previews.length : 1,
      )
      return (
        <PreviewWrapper>
          <PreviewImage
            src={
              'previews' in selectedResumeDetails
                ? selectedResumeDetails.previews[currentPage - 1].url
                : ''
            }
          />
        </PreviewWrapper>
      )
    } else {
      return resume ? (
        <Preview
          {...{ height, width, type: DocumentTypes.resume }}
          document={resume}
          documentId={resume.id}
          ref={previewRef}
          onPaginationChange={handlePaginationStateChange}
        />
      ) : null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handlePaginationStateChange, height, resume, selectedResumeDetails, currentPage, width])

  return (
    <ModalOverlay
      onClick={handleClosePreview}
      overlayFadeDuration={150}
      contentSlideDuration={150}
      fullScreen={true}
    >
      <Container ref={containerRef}>
        <HeaderContainer width={width}>
          <BackButton onClick={handleClosePreview}>
            <BackIcon />
            {i18n.t(`${LOCALIZATION_KEY}.resume_preview_modal.back_button_text`)}
          </BackButton>
          <Logo />
          <EditButton onClick={handleEditResumeClick}>
            {i18n.t(`${LOCALIZATION_KEY}.resume_preview_modal.edit_resume_button_text`)}
          </EditButton>
        </HeaderContainer>
        <PreviewContainer>
          <ContentInner ref={contentRef}>
            <PreviewBox ref={previewBoxRef}>{renderResume}</PreviewBox>
          </ContentInner>
          <Pagination>
            <PaginationPrev onClick={handlePreviousPageClick} isDisabled={!hasPreviousPage}>
              <Icon.Chevron />
            </PaginationPrev>
            <PaginationCounter>
              {currentPage} / {totalPagesNumber}
            </PaginationCounter>
            <PaginationNext onClick={handleNextPageClick} isDisabled={!hasNextPage}>
              <Icon.Chevron />
            </PaginationNext>
          </Pagination>
        </PreviewContainer>
        {isTREditModalOpen && selectedResumeDetails && 'id' in selectedResumeDetails && (
          <TREditModal id={selectedResumeDetails.id} />
        )}
      </Container>
    </ModalOverlay>
  )
}
